import { useCallback, useEffect, useRef, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { getTime } from 'date-fns';
import { mutate } from 'swr';

import {
  CARD_INFO_DISPLAY_MAP,
  FontSize,
  FontWeight,
  LocalStorageKeys,
  STATUSES,
  TEST_IDS,
} from 'commons';
import { Text } from 'components';
import {
  Template,
  checkConsignmentRevoked,
  checkIfConsignmentCanBeNotified,
  formatMillisecondsToHours,
  getLocalStorageItem,
  sendNotification,
  swrKeys,
  updateNotifiedConsignments,
} from 'helpers';
import {
  useAcceptConsignment,
  useAuth,
  useSuperExpressAudio,
  useUpdateConsignments,
} from 'hooks';
import { recaptchaService } from 'services';
import notificationSound from 'static/sound/notification.mp3';
import {
  Consignment,
  Consignment as ConsignmentType,
  DashboardCardType,
} from 'types';
import { ConsignmentsListStore } from 'zustand-store';

import { DashboardConsignment, Loader } from '../components';
import * as S from '../styles';
import { ConsignmentListOverlay } from './components';

interface Props {
  setSelectedConsignment: (consignment: ConsignmentType | null) => void;
  setIsBot: (isBot: boolean) => void;
  selectedConsignment: ConsignmentType | null;
  setSuperExpressModal: (open: boolean) => void;
  setSuperExpressConsignments: (consignments: ConsignmentType[]) => void;
}

const ONE_HOUR_TIMESTAMP = 60 * 60000;
const { AVAILABLE_FOR_STORE } = STATUSES;
const SUPER_EXPRESS_ALLOWED_STATUSES = [
  STATUSES.AVAILABLE_FOR_STORE.value,
  STATUSES.RESERVED_BY_STORE.value,
];

const isSuperExpress = (consignment: Consignment) =>
  consignment.isSuperExpress &&
  SUPER_EXPRESS_ALLOWED_STATUSES.includes(consignment.consignmentStatus);

export function DashboardConsignmentList({
  setSelectedConsignment,
  setIsBot,
  selectedConsignment,
  setSuperExpressModal,
  setSuperExpressConsignments,
}: Props) {
  const { selectedStore } = useAuth();
  const navigate = useNavigate();
  const brand = selectedStore.brand || '';
  const store = selectedStore.name || '';
  const firstRender = useRef(true);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { acceptConsignment } = useAcceptConsignment();
  const { playAudio: playSuperExpressAudio } = useSuperExpressAudio();
  const [lastModification, setLastModification] = useState<
    number | undefined
  >();
  const [showOverlay, setShowOverlay] = useState(false);

  const isSoundNotificationActivated = getLocalStorageItem(
    LocalStorageKeys.SoundNotification,
    true
  );

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) return;

    executeRecaptcha('ConsignmentDetails').then(async token => {
      const score = await recaptchaService.getScore(token);

      if (score < 0.5) {
        setIsBot(true);
      }
    });
  }, [executeRecaptcha, setIsBot]);

  const {
    consignments: consignmentData,
    dashboardSearch,
    dashboardFilter,
    isValidating,
    isLoading,
    mutate: mutateConsignments,
  } = ConsignmentsListStore.useGetConsignments();

  const { data: lastModificationData } = useUpdateConsignments();

  useEffect(() => {
    const hasSuperExpress = consignmentData?.some(consignment =>
      isSuperExpress(consignment)
    );

    setShowOverlay(hasSuperExpress);
  }, [consignmentData]);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;

      return;
    }

    mutateConsignments();
    setSelectedConsignment(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardSearch, dashboardFilter]);

  useEffect(() => {
    if (selectedConsignment) {
      const selectedConsignmentOnList = consignmentData?.find(
        consignment =>
          consignment.consignmentCode === selectedConsignment?.consignmentCode
      );

      const selectedConsignmentChangeStatus = Boolean(
        selectedConsignment.consignmentStatus !==
        selectedConsignmentOnList?.consignmentStatus
      );

      if (selectedConsignmentChangeStatus) {
        mutate(
          swrKeys.consignmentDetails({
            consignmentCode: selectedConsignment?.consignmentCode,
          })
        );
      }
    }
  }, [consignmentData, selectedConsignment]);

  useEffect(() => {
    if (lastModificationData !== lastModification) {
      setLastModification(lastModificationData);

      if (lastModificationData) {
        mutateConsignments();
        mutate(swrKeys.dashboardSummary({ brand, store }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutateConsignments, lastModificationData]);

  const newAvailableConsignments = consignmentData?.filter(consignment => {
    const isRevokedByCurrentStore = checkConsignmentRevoked({
      consignment,
      selectedStore,
    });
    const isStatusAvailable =
      consignment.consignmentStatus === AVAILABLE_FOR_STORE.value;

    return !isRevokedByCurrentStore && isStatusAvailable;
  });

  useEffect(() => {
    const { alreadyNotifiedConsignments, consignmentsToNotify } =
      checkIfConsignmentCanBeNotified(
        newAvailableConsignments,
        LocalStorageKeys.NotifiedConsignments
      );

    if (!consignmentsToNotify.length) return;

    const currentTime = new Date();

    if (consignmentsToNotify.length > 1) {
      const consigmentsSortedByTime = consignmentsToNotify.sort(
        (a, b) => a.endTimer.getTime() - b.endTimer.getTime()
      );

      const { endTimer } = consigmentsSortedByTime[0];

      const timeToExpire = endTimer.getTime() - currentTime.getTime();

      const { hours, minutes } = formatMillisecondsToHours(timeToExpire);

      const timeTemplate = `${
        hours !== '00' ? `${hours}h e` : ''
      } ${minutes} minutos`;

      const pathToWaitingAttendanceFilter =
        CARD_INFO_DISPLAY_MAP[DashboardCardType.WaitingAttendance].navigatePath;

      sendNotification({
        variant: Template.multiConsignmentAvailable,
        options: {
          consignmentQuantity: consignmentsToNotify.length,
          consignmentExpireHour: timeTemplate,
        },
        onClick: () => navigate(pathToWaitingAttendanceFilter),
      });

      updateNotifiedConsignments(
        alreadyNotifiedConsignments,
        consignmentsToNotify,
        LocalStorageKeys.NotifiedConsignments
      );

      return;
    }

    function handleNotificationClick(consignmentCode: string) {
      window.location.hash = consignmentCode;
    }

    const consignmentsToExpire = consignmentsToNotify.filter(consignment => {
      const { endTimer, consignmentCode } = consignment;
      const finalTimer = getTime(endTimer);
      const expiringTime = finalTimer - Number(currentTime);

      if (expiringTime <= ONE_HOUR_TIMESTAMP) {
        sendNotification({
          variant: Template.consignmentExpiring,
          options: {
            consignmentCode: consignmentCode,
          },
          onClick: () => handleNotificationClick(consignmentCode),
        });

        updateNotifiedConsignments(
          alreadyNotifiedConsignments,
          [consignment],
          LocalStorageKeys.NotifiedExpiringConsignments
        );

        return false;
      }

      return true;
    });

    const consignmentsTimeouts = consignmentsToExpire.map(consignment => {
      const { endTimer, consignmentCode } = consignment;
      const finalTimer = getTime(endTimer);

      const expiringTime = finalTimer - Number(currentTime);
      const notificationScheduledTime = expiringTime - ONE_HOUR_TIMESTAMP;

      return setTimeout(() => {
        sendNotification({
          variant: Template.consignmentExpiring,
          options: {
            consignmentCode: consignmentCode,
          },
          onClick: () => handleNotificationClick(consignmentCode),
        });

        updateNotifiedConsignments(
          alreadyNotifiedConsignments,
          [consignment],
          LocalStorageKeys.NotifiedExpiringConsignments
        );
      }, notificationScheduledTime);
    });

    return () => {
      consignmentsTimeouts.forEach(timeout => clearTimeout(timeout));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newAvailableConsignments]);

  useEffect(() => {
    const { alreadyNotifiedConsignments, consignmentsToNotify } =
      checkIfConsignmentCanBeNotified(
        newAvailableConsignments,
        LocalStorageKeys.NotifiedConsignments
      );

    if (!consignmentsToNotify.length) return;

    const consignmentCodes = consignmentsToNotify
      .map(consignment => consignment.consignmentCode)
      .join(',');

    const handleNotificationClick = () =>
      (window.location.hash = consignmentCodes);

    sendNotification({
      variant: Template.consignmentAvailable,
      onClick: handleNotificationClick,
    });

    const superExpressConsignments = consignmentsToNotify.filter(
      consignment => consignment.isSuperExpress
    );

    const hasSuperExpress = superExpressConsignments.length > 0;

    if (hasSuperExpress) {
      setSuperExpressConsignments(superExpressConsignments);
      setSuperExpressModal(true);
      setShowOverlay(true);

      if (isSoundNotificationActivated) {
        playSuperExpressAudio();
      }
    } else {
      toast.success('Novo Pedido Disponível', {
        delay: 500,
        onClick: handleNotificationClick,
      });

      if (isSoundNotificationActivated) {
        const notificationAudio = new Audio(notificationSound);
        notificationAudio.play();
      }
    }

    updateNotifiedConsignments(
      alreadyNotifiedConsignments,
      consignmentsToNotify,
      LocalStorageKeys.NotifiedConsignments
    );
  }, [
    newAvailableConsignments,
    isSoundNotificationActivated,
    setSuperExpressModal,
    setSuperExpressConsignments,
    playSuperExpressAudio,
  ]);

  if (isLoading) {
    return (
      <S.Wrapper>
        <Loader />
      </S.Wrapper>
    );
  }

  if (!consignmentData || consignmentData.length === 0) {
    setSelectedConsignment(null);

    return (
      <S.TextWrapper>
        <Text size={FontSize.font16} weight={FontWeight.medium}>
          Nenhum pedido encontrado
        </Text>
      </S.TextWrapper>
    );
  }

  const hasSelectedConsignmentOnList = consignmentData.some(
    consignment =>
      consignment.consignmentCode === selectedConsignment?.consignmentCode
  );

  if (selectedConsignment && !hasSelectedConsignmentOnList) {
    toast.error(
      `Pedido ${selectedConsignment.consignmentCode} não está mais disponível`,
      { autoClose: false, hideProgressBar: false }
    );

    setSelectedConsignment(null);
  }

  const consignmentsSuperExpressOnTop = [...consignmentData].sort(
    (current, next) =>
      Number(next.isSuperExpress) - Number(current.isSuperExpress)
  );

  const renderList = consignmentsSuperExpressOnTop.map(formattedConsignment => {
    const consignment = formattedConsignment;
    const { consignmentCode, consignmentStatus: status } = consignment;
    const isSelected = selectedConsignment?.consignmentCode === consignmentCode;

    async function handleSelectConsignment() {
      handleReCaptchaVerify();

      if (status === AVAILABLE_FOR_STORE.value) {
        await acceptConsignment({
          consignment,
          setSelectedConsignment,
        });
      } else {
        setSelectedConsignment(consignment);
      }
    }

    return (
      <DashboardConsignment
        key={consignmentCode}
        action={isSelected ? () => {} : handleSelectConsignment}
        consignment={consignment}
        isSelected={isSelected}
      />
    );
  });

  return (
    <S.Main>
      <S.Wrapper
        data-testid={TEST_IDS.CONSIGNMENTS_LIST}
        style={{ overflowY: showOverlay ? 'hidden' : 'auto' }}
      >
        {renderList}
      </S.Wrapper>

      {showOverlay && (
        <ConsignmentListOverlay
          onClose={() => setShowOverlay(false)}
          superExpressCount={
            consignmentData.filter(c => c.isSuperExpress).length
          }
        />
      )}

      {isValidating && !isLoading && (
        <S.ValidationWrapper>
          <Text weight={FontWeight.bold}>Atualizando listagem...</Text>
        </S.ValidationWrapper>
      )}
    </S.Main>
  );
}
